import { Component, ErrorInfo, ReactNode } from "react";
import { Alert, AlertDescription } from "@tellescope/ui";
import { i18n } from "@tellescope/i18n";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

/**
 * Error boundary component to catch and display React errors
 */
export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
  }

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="p-4">
          <Alert className="border-destructive bg-destructive/10">
            <AlertDescription className="text-destructive">
              <div className="space-y-2">
                <div className="font-semibold">{i18n.t("Something went wrong", { ns: "common" })}</div>
                <div className="text-sm">
                  {this.state.error?.message || i18n.t("An unexpected error occurred", { ns: "common" })}
                </div>
                <button
                  onClick={() => this.setState({ hasError: false, error: undefined })}
                  className="text-sm underline hover:no-underline"
                >
                  {i18n.t("Try again", { ns: "common" })}
                </button>
              </div>
            </AlertDescription>
          </Alert>
        </div>
      );
    }

    return this.props.children;
  }
}
